import { getUserConfig, getUserInfoUrl } from 'modules/cbre-flow-api-ducks';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { setSiteInfoSelected } from 'modules/cbre-flow-site-location';
import { useHistory } from 'react-router-dom';
import { getUserAccess } from 'modules/cbre-flow-api-ducks/selectors';
import { useSessionStorage } from 'components/useSessionStorage';
import LoadingConfigScreen from 'components/ConfigLoader/loading-config-screen';
import ConfigErrorScreen from 'components/ConfigLoader/config-error-screen';
import { setCommonAlerts } from 'modules/cbre-flow-Common/actions';
import { getSiteList, setSiteInfo } from 'modules/cbre-flow-site-location/ducks/actions';
import { getSiteInfoUrl, setSiteInfoUrl } from 'modules/cbre-flow-site-location/ducks/selectors';
import { fetchViews, getCurrentView, getViewManagementApiUrl, setViewAndUserConfigReady } from 'modules/cbre-flow-view-management';
import { HTTP_STATUS } from 'util/apiService';

interface ConfigProviderProps {
    children: React.ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = React.useState(true);
    const [isRedirecting, setIsRedirecting] = React.useState(false);
    const [configErrorCode, setConfigErrorCode] = React.useState<number | null>(null);
    const userUrl = useSelector(getUserInfoUrl);
    const history = useHistory();
    const fetchViewUrl = useSelector(getViewManagementApiUrl);
    const currentView = useSelector(getCurrentView);

    const validateAndUpdateUserHasAnySiteAccess = async (userDetails: any): Promise<boolean> => {
        const { currentContext, clients } = userDetails || {};
        if(currentContext.isPortFolioDefaultViewCreated) {
            return true;
        }
        return false;
    }

    const loadConfig = async () => {
        setLoading(true);
        setConfigErrorCode(null);
        dispatch(setViewAndUserConfigReady(false));

        try {
            const userConfig: any = await dispatch(getUserConfig({ apiUrl: `${userUrl}` }));
            if(userConfig?.code && HTTP_STATUS.OK !== userConfig.code) {
                setConfigErrorCode(userConfig?.code === 400 ? 400 : 500);
                setLoading(false);
                return;
            }

            const { userDetails = {}, hasRequestedSiteAccess } = userConfig || {};
            
            const isUserHasSiteAccess = await validateAndUpdateUserHasAnySiteAccess(userDetails)
            if(isUserHasSiteAccess) {
                if (fetchViewUrl) {
                    const fetchViewsResponse: any = await dispatch(fetchViews({ apiUrl: `${fetchViewUrl}` }));
                    dispatch(setViewAndUserConfigReady(fetchViewsResponse?.statusCode === HTTP_STATUS.OK));
                }                
                setLoading(false);
            }else if (hasRequestedSiteAccess) {
                dispatch(setViewAndUserConfigReady(false));
                setLoading(false);
                setIsRedirecting(true);
                history.push('/elis/site-pending-access');
            }else{
                dispatch(setViewAndUserConfigReady(false));
                setLoading(false);
                setIsRedirecting(true);
                history.push('/elis/location');
            }

        } catch (error) {
            dispatch(setViewAndUserConfigReady(false));
            setConfigErrorCode(500);
            setLoading(false);
        }
    }
    useEffect(() => {
        loadConfig();        
    }, []);

    if (configErrorCode && !isRedirecting) {
        return <ConfigErrorScreen code={configErrorCode} onRetry={loadConfig} />;
    }

    if ((loading || !currentView) && !isRedirecting) {
        return <LoadingConfigScreen />;
    }

    return (
        <div style={{ height: '100%', width: '100%' }}>{children}</div>
    );
};
